import { ChipProps } from '@c2fo/liquidity';
import { LockedInvoice, LockedInvoiceSearchFilters } from '@/enterprise/data/useLockedInvoices';
import { LockedInvoiceStatsSearchFilters } from '@/enterprise/data/useLockedInvoiceStats';
import { IneligibleInvoiceFilterOptions, IneligibleInvoiceUrlState } from './types';

export const getIneligibilityReasonConfig = (
  lockedInvoice: LockedInvoice
): { labelKey: string; chipType: ChipProps['type']; tooltipKey: string } | undefined => {
  const { lockedReason } = lockedInvoice;
  const { isCoveringAdjustment, isPastDue, isReserved, isMakerExcluded, isIneligibleAr, isIneligibleEp } =
    lockedReason ?? {};
  if (isPastDue) {
    return {
      labelKey: 'taker.invoiceStatus.isPastDue',
      chipType: 'error',
      tooltipKey: 'invoiceCentral.pastDue.tooltip',
    };
  }

  if (isCoveringAdjustment) {
    return {
      labelKey: 'taker.invoiceStatus.isAdjusted',
      chipType: 'default',
      tooltipKey: 'invoiceCentral.coveringAdjustment.tooltip',
    };
  }

  if (isReserved || isMakerExcluded || isIneligibleAr || isIneligibleEp) {
    return {
      labelKey: 'taker.invoiceStatus.isMakerExcluded',
      chipType: 'secondary',
      tooltipKey: 'invoiceCentral.reservedByCustomer.tooltip',
    };
  }
};

export const mergeFilters = ({
  prevFilters,
  newFilters,
}: {
  prevFilters?: IneligibleInvoiceFilterOptions;
  newFilters?: IneligibleInvoiceFilterOptions;
}) => {
  if (!newFilters) {
    return prevFilters ?? {};
  }

  let mergedFilters = prevFilters ?? {};

  // Helper function to merge filters with range values (from/to)
  const mergeRangeFilter = <K extends Exclude<keyof IneligibleInvoiceFilterOptions, 'lockedReason'>>(filterKey: K) => {
    if (!newFilters[filterKey]) {
      return;
    }

    // Date filters from/to properties are strings, but amount is number
    // Create type parameter for the filter type in this call
    type FilterType = IneligibleInvoiceFilterOptions[K];
    const newFilter = newFilters[filterKey] as FilterType;
    const { [filterKey]: prevFilter, ...rest } = mergedFilters;

    let mergedFilter = newFilter;
    if (prevFilter) {
      mergedFilter = {
        ...(newFilter?.from !== null && { from: newFilter?.from ?? prevFilter.from }),
        ...(newFilter?.to !== null && { to: newFilter?.to ?? prevFilter.to }),
      } as FilterType;
    }

    mergedFilters = {
      ...rest,
      ...((mergedFilter?.from || mergedFilter?.to) && { [filterKey]: mergedFilter }),
    };
  };

  mergeRangeFilter('dueDate');
  mergeRangeFilter('originalAmount');

  if (newFilters.lockedReason) {
    const { lockedReason: prevLockedReason, ...rest } = mergedFilters;

    const mergedLockedReason = {
      ...prevLockedReason,
      ...newFilters.lockedReason,
    };

    // only pass through lockedReason properties if they are true
    const cleanedLockedReason = Object.fromEntries(Object.entries(mergedLockedReason).filter(([_, value]) => value));

    mergedFilters = {
      ...rest,
      ...(Object.keys(cleanedLockedReason).length > 0 && { lockedReason: cleanedLockedReason }),
    };
  }

  return mergedFilters;
};

export const mapUrlStateToApiSearchFilters = ({
  urlState,
}: {
  urlState: IneligibleInvoiceUrlState;
}): LockedInvoiceSearchFilters | LockedInvoiceStatsSearchFilters => {
  const search = {
    // Even though searchValue is typed as string, useUrlState is parsing it as a number when it's just digits
    // e.g. "123" becomes 123, so cast it to string for API
    ...(urlState.searchValue && { voucherPatterns: [String(urlState.searchValue)] }),
    ...((!!urlState.filters?.originalAmount?.from || !!urlState.filters?.originalAmount?.to) && {
      originalAmount: [
        {
          ...(!!urlState.filters.originalAmount?.from && { gte: urlState.filters.originalAmount.from }),
          ...(!!urlState.filters.originalAmount?.to && { lte: urlState.filters.originalAmount.to }),
        },
      ],
    }),
    ...((!!urlState.filters?.dueDate?.from || !!urlState.filters?.dueDate?.to) && {
      dueDate: [
        {
          ...(!!urlState.filters.dueDate?.from && { from: urlState.filters.dueDate.from }),
          ...(!!urlState.filters.dueDate?.to && { to: urlState.filters.dueDate.to }),
        },
      ],
    }),
    ...(!!urlState.filters?.lockedReason && {
      lockedReason: {
        ...(urlState.filters.lockedReason?.isCoveringAdjustment && { isCoveringAdjustment: true }),
        ...(urlState.filters.lockedReason?.isPastDue && { isPastDue: true }),
        ...(urlState.filters.lockedReason?.isReservedByCustomer && {
          isReserved: true,
          isMakerExcluded: true,
          isIneligibleAr: true,
          isIneligibleEp: true,
        }),
      },
    }),
  };

  return Object.keys(search).length > 0 ? search : undefined;
};
